import {
  type PaymentMethod,
  PURCHASE_SORT_FIELDS,
  PURCHASE_VIEWS,
  type PurchaseListQuery,
  paymentMethodSchema,
  SUPPLIER_PAYMENT_SORT_FIELDS,
  SUPPLIER_PAYMENT_VIEWS,
  SUPPLIER_REFUND_SORT_FIELDS,
  SUPPLIER_REFUND_VIEWS,
  SUPPLIER_RETURN_SORT_FIELDS,
  SUPPLIER_RETURN_VIEWS,
  type SupplierReturnKind,
  supplierCreditSourceTypeSchema,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'
import { PAGE_SIZES } from '../sales/search'

/**
 * The purchasing lists live in the URL (shareable, back-button safe), like the sales lists. Every
 * key is optional and an invalid value silently falls back to the default instead of breaking the
 * page. The view tabs are the contract's (pages/purchasing.md §7.3); `all` is never written.
 */
type PageSize = (typeof PAGE_SIZES)[number]

const page = z.number().int().min(1).optional().catch(undefined)
const pageSize = z
  .number()
  .refine((n): n is PageSize => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)
const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)
// A hand-written `?q=4471` arrives as a number (the router parses JSON): keep it as text.
const q = z.coerce.string().trim().max(200).optional().catch(undefined)
const uuid = z.uuid().optional().catch(undefined)
const mine = z.literal('me').optional().catch(undefined)
const method = paymentMethodSchema.optional().catch(undefined)

function sortOf<const F extends string>(fields: readonly F[]) {
  const valid = (value: string): value is F | `-${F}` =>
    (fields as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
  return {
    valid,
    schema: z.string().refine(valid).optional().catch(undefined),
  }
}

/** The views a list can open on (its first, `all`, is the default and never in the URL). */
function viewsOf<const V extends readonly [string, ...string[]]>(views: V) {
  const [, ...rest] = views
  return rest as unknown as readonly Exclude<V[number], 'all'>[]
}

// ── purchases ────────────────────────────────────────────────────────────────

export const PURCHASE_SORT = sortOf(PURCHASE_SORT_FIELDS)
export const PURCHASE_DEFAULT_SORT = '-purchaseDate'
export const PURCHASE_LIST_VIEWS = viewsOf(PURCHASE_VIEWS)
export type PurchaseListView = (typeof PURCHASE_LIST_VIEWS)[number]

/**
 * Each view reads in its own order: what is owed is chased oldest due first; drafts (the open
 * orders) oldest first; the rest newest first.
 */
export function purchaseDefaultSort(view: PurchaseListView | 'all' | undefined): string {
  if (view === 'open' || view === 'overdue') return 'dueDate'
  if (view === 'drafts') return 'purchaseDate'
  return PURCHASE_DEFAULT_SORT
}

export const purchaseSearchSchema = z.object({
  page,
  pageSize,
  sort: PURCHASE_SORT.schema,
  q,
  view: z
    .enum(PURCHASE_LIST_VIEWS as unknown as [PurchaseListView, ...PurchaseListView[]])
    .optional()
    .catch(undefined),
  supplierId: uuid,
  /** The requesting unit and everything under it. */
  orgUnitId: uuid,
  /** Purchases with a line tagged to this project. */
  projectId: uuid,
  from: isoDate,
  to: isoDate,
  /** "Mine" on the Drafts view: the drafts I recorded. */
  createdBy: mine,
})
export type PurchaseSearch = z.infer<typeof purchaseSearchSchema>

export type PurchaseListParams = Omit<PurchaseListQuery, 'page' | 'pageSize' | 'open' | 'paymentStatus'> & {
  page: number
  pageSize: number
  sort: string
  /** Posted purchases with an open balance. */
  open?: boolean | undefined
  paymentStatus?: 'OVERDUE' | undefined
}

/** The list call of one view (the summary counts the same filters). */
export function purchaseViewFilter(view: PurchaseListView | 'all' | undefined) {
  return {
    status: view === 'drafts' ? ('DRAFT' as const) : view === 'void' ? ('VOID' as const) : undefined,
    open: view === 'open' ? true : undefined,
    paymentStatus: view === 'overdue' ? ('OVERDUE' as const) : undefined,
  }
}

export function toPurchaseListQuery(search: PurchaseSearch): PurchaseListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? purchaseDefaultSort(search.view),
    q: search.q || undefined,
    ...purchaseViewFilter(search.view),
    supplierId: search.supplierId,
    orgUnitId: search.orgUnitId,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    createdBy: search.createdBy,
  }
}

/** The summary's filters: the list's, without the view, paging and sort. */
export function toPurchaseSummaryQuery(query: PurchaseListParams) {
  return {
    q: query.q,
    supplierId: query.supplierId,
    orgUnitId: query.orgUnitId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}
export type PurchaseSummaryParams = ReturnType<typeof toPurchaseSummaryQuery>

/**
 * New purchase: `?supplierId=` preselects the supplier; `?from=` copies a purchase (a repeat order);
 * `?projectId=` (opened from a project) starts the lines on that project; `?capture=true` (AC1, "New from
 * a document" on the list) opens "Fill from a list or a document" at once.
 */
export const newPurchaseSearchSchema = z.object({
  supplierId: uuid,
  from: uuid,
  projectId: uuid,
  capture: z.boolean().optional().catch(undefined),
})

// ── supplier payments ────────────────────────────────────────────────────────

export const PAYMENT_SORT = sortOf(SUPPLIER_PAYMENT_SORT_FIELDS)
export const PAYMENT_DEFAULT_SORT = '-paymentDate'
export const PAYMENT_LIST_VIEWS = viewsOf(SUPPLIER_PAYMENT_VIEWS)
export type PaymentListView = (typeof PAYMENT_LIST_VIEWS)[number]

export const paymentSearchSchema = z.object({
  page,
  pageSize,
  sort: PAYMENT_SORT.schema,
  q,
  view: z
    .enum(PAYMENT_LIST_VIEWS as unknown as [PaymentListView, ...PaymentListView[]])
    .optional()
    .catch(undefined),
  supplierId: uuid,
  method,
  treasuryAccountId: uuid,
  from: isoDate,
  to: isoDate,
})
export type PaymentSearch = z.infer<typeof paymentSearchSchema>

export interface PaymentListParams {
  page: number
  pageSize: number
  sort: string
  q?: string | undefined
  status?: 'POSTED' | 'VOID' | undefined
  hasOpenCredit?: boolean | undefined
  supplierId?: string | undefined
  method?: PaymentMethod | undefined
  treasuryAccountId?: string | undefined
  from?: string | undefined
  to?: string | undefined
}

/** With credit: the advances, largest first is not what is chased: oldest first. */
export function paymentDefaultSort(view: PaymentListView | 'all' | undefined): string {
  return view === 'withCredit' ? 'paymentDate' : PAYMENT_DEFAULT_SORT
}

export function paymentViewFilter(view: PaymentListView | 'all' | undefined) {
  return {
    status: view === 'void' ? ('VOID' as const) : undefined,
    hasOpenCredit: view === 'withCredit' ? true : undefined,
  }
}

export function toPaymentListQuery(search: PaymentSearch): PaymentListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? paymentDefaultSort(search.view),
    q: search.q || undefined,
    ...paymentViewFilter(search.view),
    supplierId: search.supplierId,
    method: search.method,
    treasuryAccountId: search.treasuryAccountId,
    from: search.from,
    to: search.to,
  }
}

export function toPaymentSummaryQuery(query: PaymentListParams) {
  return {
    q: query.q,
    supplierId: query.supplierId,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    from: query.from,
    to: query.to,
  }
}
export type PaymentSummaryParams = ReturnType<typeof toPaymentSummaryQuery>

/** Pay a supplier: `?supplierId=` preselects the supplier, `?purchaseId=` settles that purchase. */
/** `replacesChequeId`: a replacement for that bounced or cancelled cheque (ADR-046). */
export const newPaymentSearchSchema = z.object({ supplierId: uuid, purchaseId: uuid, replacesChequeId: uuid })

// ── supplier returns and allowances ──────────────────────────────────────────

export const RETURN_SORT = sortOf(SUPPLIER_RETURN_SORT_FIELDS)
export const RETURN_DEFAULT_SORT = '-returnDate'
export const RETURN_KINDS = ['RETURN', 'ALLOWANCE'] as const satisfies readonly SupplierReturnKind[]
export const RETURN_LIST_VIEWS = viewsOf(SUPPLIER_RETURN_VIEWS)
export type ReturnListView = (typeof RETURN_LIST_VIEWS)[number]

export const returnSearchSchema = z.object({
  page,
  pageSize,
  sort: RETURN_SORT.schema,
  q,
  view: z
    .enum(RETURN_LIST_VIEWS as unknown as [ReturnListView, ...ReturnListView[]])
    .optional()
    .catch(undefined),
  kind: z.enum(RETURN_KINDS).optional().catch(undefined),
  supplierId: uuid,
  purchaseId: uuid,
  from: isoDate,
  to: isoDate,
})
export type ReturnSearch = z.infer<typeof returnSearchSchema>

export interface ReturnListParams {
  page: number
  pageSize: number
  sort: string
  q?: string | undefined
  kind?: SupplierReturnKind | undefined
  status?: 'DRAFT' | 'POSTED' | 'VOID' | undefined
  hasOpenCredit?: boolean | undefined
  supplierId?: string | undefined
  purchaseId?: string | undefined
  from?: string | undefined
  to?: string | undefined
}

export function returnDefaultSort(view: ReturnListView | 'all' | undefined): string {
  return view === 'withCredit' || view === 'drafts' ? 'returnDate' : RETURN_DEFAULT_SORT
}

export function returnViewFilter(view: ReturnListView | 'all' | undefined) {
  return {
    status: view === 'drafts' ? ('DRAFT' as const) : view === 'void' ? ('VOID' as const) : undefined,
    hasOpenCredit: view === 'withCredit' ? true : undefined,
  }
}

export function toReturnListQuery(search: ReturnSearch): ReturnListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? returnDefaultSort(search.view),
    q: search.q || undefined,
    kind: search.kind,
    ...returnViewFilter(search.view),
    supplierId: search.supplierId,
    purchaseId: search.purchaseId,
    from: search.from,
    to: search.to,
  }
}

export function toReturnSummaryQuery(query: ReturnListParams) {
  return {
    q: query.q,
    kind: query.kind,
    supplierId: query.supplierId,
    purchaseId: query.purchaseId,
    from: query.from,
    to: query.to,
  }
}
export type ReturnSummaryParams = ReturnType<typeof toReturnSummaryQuery>

/**
 * New supplier return: `?purchaseId=` returns goods from that purchase (a return by default),
 * `?supplierId=` preselects the supplier, `?kind=` picks the kind.
 */
export const newReturnSearchSchema = z.object({
  purchaseId: uuid,
  supplierId: uuid,
  kind: z.enum(RETURN_KINDS).optional().catch(undefined),
})

// ── payables reports ─────────────────────────────────────────────────────────

export const payablesAgingSearchSchema = z.object({ asOf: isoDate })

// ── supplier refunds ─────────────────────────────────────────────────────────

export const SUPPLIER_REFUND_SORT = sortOf(SUPPLIER_REFUND_SORT_FIELDS)
export const SUPPLIER_REFUND_DEFAULT_SORT = '-refundDate'
export const REFUND_LIST_VIEWS = viewsOf(SUPPLIER_REFUND_VIEWS)
export type RefundListView = (typeof REFUND_LIST_VIEWS)[number]

export const supplierRefundSearchSchema = z.object({
  page,
  pageSize,
  sort: SUPPLIER_REFUND_SORT.schema,
  q,
  view: z
    .enum(REFUND_LIST_VIEWS as unknown as [RefundListView, ...RefundListView[]])
    .optional()
    .catch(undefined),
  supplierId: uuid,
  method,
  treasuryAccountId: uuid,
  from: isoDate,
  to: isoDate,
})
export type SupplierRefundSearch = z.infer<typeof supplierRefundSearchSchema>
export type SupplierRefundListParams = Omit<PaymentListParams, 'hasOpenCredit'>

export function toSupplierRefundListQuery(search: SupplierRefundSearch): SupplierRefundListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? SUPPLIER_REFUND_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.view === 'void' ? 'VOID' : undefined,
    supplierId: search.supplierId,
    method: search.method,
    treasuryAccountId: search.treasuryAccountId,
    from: search.from,
    to: search.to,
  }
}

export function toRefundSummaryQuery(query: SupplierRefundListParams) {
  return {
    q: query.q,
    supplierId: query.supplierId,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    from: query.from,
    to: query.to,
  }
}
export type RefundSummaryParams = ReturnType<typeof toRefundSummaryQuery>

/**
 * Record a supplier refund: `?supplierId=` preselects the supplier; `?sourceType=&sourceId=` (from a
 * payment or return page) fills in that credit's remainder.
 */
export const newSupplierRefundSearchSchema = z.object({
  supplierId: uuid,
  sourceType: supplierCreditSourceTypeSchema.optional().catch(undefined),
  sourceId: uuid,
})
